import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSearch, faXmark } from '@fortawesome/free-solid-svg-icons'
import {
  ENTITY_DROP_EVENT,
  getEntityDrag,
  getEntityDragPointer,
  subscribeEntityDrag,
  type EntityDragPayload,
} from './controlCenter/entityDrag'
import { isInsideKioskVirtualKeyboard } from '../utils/kioskVirtualKeyboard'

export type EntitySearchItem = {
  id: string
  label: string
  group?: string
  /** Extra text matched by search but not shown as the primary label. */
  keywords?: string
}

type EntitySearchProps = {
  items: EntitySearchItem[]
  value: string
  onChange: (id: string) => void
  placeholder?: string
  allowClear?: boolean
  emptyHint?: string
  disabled?: boolean
  className?: string
  'aria-label'?: string
  /**
   * When true, accepts map-entity pointer drops (Control Center drag-from-marker).
   * Nearby-target fields should leave this false.
   */
  acceptEntityDrop?: boolean
}

type PopupPos = { bottom: number; left: number; width: number; maxHeight: number }

function itemMatches(item: EntitySearchItem, query: string): boolean {
  if (!query) return true
  const haystack = [item.label, item.group, item.keywords]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
  return haystack.includes(query)
}

/**
 * Reusable combobox for picking an entity from a parent-supplied list.
 * Results open in a fixed portal above the field so parent layouts (e.g. Operation
 * panel) do not grow or clip the list.
 */
export function EntitySearch({
  items,
  value,
  onChange,
  placeholder = 'Search…',
  allowClear = false,
  emptyHint = 'No matches.',
  disabled = false,
  className = '',
  'aria-label': ariaLabel = 'Search entities',
  acceptEntityDrop = false,
}: EntitySearchProps) {
  const listId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const inputWrapRef = useRef<HTMLLabelElement>(null)
  const popupRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlight, setHighlight] = useState(0)
  const [pos, setPos] = useState<PopupPos | null>(null)
  const [dropHover, setDropHover] = useState(false)
  const itemsRef = useRef(items)
  const onChangeRef = useRef(onChange)

  useEffect(() => {
    itemsRef.current = items
  }, [items])

  useEffect(() => {
    onChangeRef.current = onChange
  }, [onChange])

  const selected = useMemo(
    () => items.find((item) => item.id === value) ?? null,
    [items, value],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return items.filter((item) => itemMatches(item, q)).slice(0, 80)
  }, [items, query])

  const grouped = useMemo(() => {
    const map = new Map<string, EntitySearchItem[]>()
    for (const item of filtered) {
      const group = item.group || 'Results'
      const list = map.get(group)
      if (list) list.push(item)
      else map.set(group, [item])
    }
    return [...map.entries()]
  }, [filtered])

  const updatePosition = useCallback(() => {
    const trigger = inputWrapRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const gap = 6
    // Anchor the popup's bottom edge just above the field — no height feedback loop.
    const bottom = Math.round(window.innerHeight - rect.top + gap)
    const maxHeight = Math.round(Math.min(224, Math.max(80, rect.top - 16)))
    const width = Math.round(rect.width)
    let left = Math.round(rect.left)
    left = Math.min(left, window.innerWidth - width - 8)
    left = Math.max(8, left)
    setPos((prev) => {
      if (
        prev &&
        prev.bottom === bottom &&
        prev.left === left &&
        prev.width === width &&
        prev.maxHeight === maxHeight
      ) {
        return prev
      }
      return { bottom, left, width, maxHeight }
    })
  }, [])

  // Keep input text in sync when selection changes externally.
  useEffect(() => {
    if (!open) {
      setQuery(selected?.label ?? '')
    }
  }, [selected, open])

  // If the selected id disappears from items, clear it.
  useEffect(() => {
    if (value && !items.some((item) => item.id === value)) {
      onChange('')
    }
  }, [items, value, onChange])

  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    updatePosition()
    const onWin = () => updatePosition()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open, filtered.length, updatePosition])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: MouseEvent) => {
      if (isInsideKioskVirtualKeyboard(event.target)) return
      const target = event.target as Node
      if (rootRef.current?.contains(target) || popupRef.current?.contains(target)) return
      setOpen(false)
      setQuery(selected?.label ?? '')
    }
    document.addEventListener('mousedown', onPointerDown)
    return () => document.removeEventListener('mousedown', onPointerDown)
  }, [open, selected])

  useEffect(() => {
    setHighlight(0)
  }, [query, open])

  // Accept Control Center map-entity pointer drops.
  useEffect(() => {
    if (!acceptEntityDrop) return
    const node = rootRef.current
    if (!node) return

    const onDrop = (event: Event) => {
      if (disabled) return
      const detail = (event as CustomEvent<EntityDragPayload>).detail
      const id = detail?.refId?.trim()
      if (!id) return
      if (!itemsRef.current.some((item) => item.id === id)) return
      onChangeRef.current(id)
      setOpen(false)
    }

    node.addEventListener(ENTITY_DROP_EVENT, onDrop)
    return () => node.removeEventListener(ENTITY_DROP_EVENT, onDrop)
  }, [acceptEntityDrop, disabled])

  useEffect(() => {
    if (!acceptEntityDrop) {
      setDropHover(false)
      return
    }
    return subscribeEntityDrag(() => {
      const drag = getEntityDrag()
      if (!drag || disabled) {
        setDropHover(false)
        return
      }
      if (!itemsRef.current.some((item) => item.id === drag.refId)) {
        setDropHover(false)
        return
      }
      const node = rootRef.current
      if (!node) {
        setDropHover(false)
        return
      }
      const rect = node.getBoundingClientRect()
      const { x, y } = getEntityDragPointer()
      const over =
        x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom
      setDropHover(over)
    })
  }, [acceptEntityDrop, disabled])

  const selectItem = (item: EntitySearchItem) => {
    onChange(item.id)
    setQuery(item.label)
    setOpen(false)
  }

  const clearSelection = () => {
    onChange('')
    setQuery('')
    setOpen(false)
    inputRef.current?.focus()
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      setHighlight((prev) => Math.min(prev + 1, Math.max(filtered.length - 1, 0)))
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (!open) {
        setOpen(true)
        return
      }
      setHighlight((prev) => Math.max(prev - 1, 0))
      return
    }
    if (event.key === 'Enter') {
      event.preventDefault()
      const item = filtered[highlight]
      if (open && item) selectItem(item)
      else setOpen(true)
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      setOpen(false)
      setQuery(selected?.label ?? '')
    }
  }

  const popup =
    open && pos
      ? createPortal(
          <div
            ref={popupRef}
            id={listId}
            role="listbox"
            className="fixed z-[1200] overflow-y-auto overscroll-contain rounded-lg border border-base-300 bg-base-100 py-1 shadow-xl"
            style={{
              bottom: pos.bottom,
              left: pos.left,
              width: pos.width,
              maxHeight: pos.maxHeight,
            }}
          >
            {filtered.length === 0 ? (
              <p className="px-3 py-2 text-xs text-base-content/50">
                {items.length === 0 ? emptyHint : 'No matches.'}
              </p>
            ) : (
              grouped.map(([group, groupItems]) => (
                <div key={group}>
                  <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-base-content/45">
                    {group}
                  </p>
                  {groupItems.map((item) => {
                    const flatIndex = filtered.indexOf(item)
                    const active = flatIndex === highlight
                    const isSelected = item.id === value
                    return (
                      <button
                        key={item.id}
                        id={`${listId}-${item.id}`}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        className={`flex w-full flex-col items-start px-3 py-1.5 text-left text-sm transition-colors ${
                          active ? 'bg-primary/15 text-base-content' : 'hover:bg-base-200'
                        } ${isSelected ? 'font-semibold' : ''}`}
                        onMouseEnter={() => setHighlight(flatIndex)}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          selectItem(item)
                        }}
                      >
                        <span className="truncate">{item.label}</span>
                      </button>
                    )
                  })}
                </div>
              ))
            )}
          </div>,
          document.body,
        )
      : null

  return (
    <div
      ref={rootRef}
      className={`relative w-full ${className}`}
      {...(acceptEntityDrop ? { 'data-cc-entity-drop': '' } : {})}
    >
      <label
        ref={inputWrapRef}
        className={`input input-bordered input-sm flex w-full items-center gap-2 transition-colors ${
          dropHover ? 'border-primary bg-primary/10' : ''
        }`}
      >
        <FontAwesomeIcon icon={faSearch} className="text-[10px] text-base-content/45" />
        <input
          ref={inputRef}
          type="text"
          className="grow bg-transparent text-sm outline-none"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={
            open && filtered[highlight] ? `${listId}-${filtered[highlight].id}` : undefined
          }
          aria-label={ariaLabel}
          disabled={disabled}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            if (!open) setOpen(true)
            if (value && e.target.value !== selected?.label) {
              onChange('')
            }
          }}
          onFocus={() => {
            setOpen(true)
            if (selected) setQuery('')
          }}
          onKeyDown={onKeyDown}
        />
        {allowClear && value ? (
          <button
            type="button"
            className="btn btn-ghost btn-xs btn-square -mr-1"
            aria-label="Clear selection"
            onClick={clearSelection}
          >
            <FontAwesomeIcon icon={faXmark} className="text-[10px]" />
          </button>
        ) : null}
      </label>
      {popup}
    </div>
  )
}

export default EntitySearch
